/**
 * The Circles nav discovery menu (Figma 4327-59773) — a caret dropdown hanging
 * off the Circles nav icon, shown to a flagged cohort of members who belong to
 * NO circle. Pitches Circles, offers joinable circles inline (up to ~9 cards
 * since CO-515 beta 2, in ONE unlabeled list: the tenured rooms first for the
 * flagged cohort, then the capped interest tail — the ordering is the hook's,
 * the headings are gone), and carries two independent dismissals.
 *
 * Chrome (panel, caret, outside-click, Escape) comes from `shared/NavPopover`;
 * this file owns only the contents.
 *
 * Nineteen layers in the Figma frame are `visible: false` and are deliberately
 * NOT built: the three Avatar + Name rows, the per-card Topic Badges row, the
 * Active/Forming status badge, the notice's "?" icon and close X, and the
 * leading/trailing icons on the opt-out button.
 *
 * Deferred-i18n (unlaunched Circles beta) — see CirclesNavMenuCard for why.
 * That carve-out covers only the copy in COPY below, which has no lang keys
 * yet; the "What are Circles?" notice consumes the drawer Overview's EXISTING
 * `overview.whatAreCircles*` keys via useTranslatedText (same strings, same
 * Smartling entries, same dismissal flag as WhatAreCirclesNotice — inlining
 * them here would diverge the two surfaces the moment translations approve).
 */
import React, { useCallback, useRef } from 'react'
import Button, { TYPES } from '../../../Button/index'
import { useTranslatedText } from '../../../StaticText/StaticTextContext'
import NavPopover from '../shared/NavPopover/index'
import type { NavPopoverCloseReason } from '../shared/NavPopover/index'
import CirclesNavMenuCard from './CirclesNavMenuCard'
import type { NavMenuOption } from './navMenuCategoryCopy'

const COPY = {
  title: 'Circles',
  new: 'NEW',
  body: "Your Circle is a small, private chat built around the topic you chose. Members share what they're watching, trade recommendations, and go deeper on the questions that brought them to Gaia.",
  reassurance: 'These are private groups you can leave anytime',
  optOut: 'I am not interested in joining a circle',
}

const TITLE_ID = 'circles-nav-menu__title'

/**
 * The "you can leave anytime" line is described ONTO every Join CTA, matching
 * OnboardingCircleSelect's picker. Reading order alone doesn't carry it: a
 * screen-reader user tabbing straight to a Join button would otherwise never
 * hear the promise the copy exists to make.
 */
const REASSURANCE_ID = 'circles-nav-menu__reassurance'

/** The popover container's DOM id — the trigger's aria-controls target. */
export const CIRCLES_NAV_MENU_ID = 'circles-nav-menu'

export interface CirclesNavMenuProps {
  isOpen: boolean
  /** Close reason forwarded from NavPopover — 'escape' restores trigger focus. */
  onClose: (reason?: NavPopoverCloseReason) => void
  options: NavMenuOption[]
  joinedIds: ReadonlySet<string>
  onJoin: (rainbowCategoryKey: number) => void
  /** A join is in flight — every card's CTA disables (single-flight guard). */
  joinPending?: boolean
  noticeDismissed: boolean
  onDismissNotice: () => void
  onOptOut: () => void
  /** The header ring glyph, injected so this component stays free of the lazy icon import. */
  symbol?: React.ReactNode
}

const CirclesNavMenuBase: React.FC<CirclesNavMenuProps> = ({
  isOpen,
  onClose,
  options,
  joinedIds,
  onJoin,
  joinPending = false,
  noticeDismissed,
  onDismissNotice,
  onOptOut,
  symbol = null,
}) => {
  // The notice's copy is already keyed (and in Smartling) for the drawer's
  // WhatAreCirclesNotice; the menu renders inside CirclesNavEntry's
  // communityCircles StaticTextProvider, so the same keys resolve here.
  const noticeTitle = useTranslatedText({ keys: ['overview', 'whatAreCirclesTitle'] })
  const noticeBody = useTranslatedText({ keys: ['overview', 'whatAreCirclesBody'] })
  const noticeDismiss = useTranslatedText({ keys: ['overview', 'dismiss'] })
  const handleOptOut = useCallback(() => {
    onOptOut()
    onClose()
  }, [onOptOut, onClose])
  // Dismissing the notice unmounts the focused dismiss button while the
  // dialog stays open — move focus to the surviving previous control so
  // keyboard focus never drops to <body> inside an open dialog (WCAG 2.4.3).
  const optOutRef = useRef<HTMLButtonElement>(null)
  const handleDismissNotice = useCallback(() => {
    onDismissNotice()
    optOutRef.current?.focus()
  }, [onDismissNotice])

  // ONE unlabeled list. The hook still orders tenured rooms ahead of the
  // capped interest tail (useCirclesNavMenu's F2 ordering), but the panel no
  // longer splits them under "For longtime members" / "By interest" headings —
  // the room names carry themselves and the headings only added chrome.
  const renderCards = (list: NavMenuOption[]) => (
    /* Explicit role: list-style none strips <ul> list semantics in
       Safari/VoiceOver, and the card count is meaningful context. */
    <ul className="circles-nav-menu__cards" role="list">
      {list.map((option) => (
        <CirclesNavMenuCard
          key={option.rainbowCategoryKey}
          option={option}
          joined={joinedIds.has(String(option.rainbowCategoryKey))}
          pending={joinPending}
          onJoin={onJoin}
          describedBy={REASSURANCE_ID}
        />
      ))}
    </ul>
  )

  return (
    <NavPopover
      isOpen={isOpen}
      onClose={onClose}
      id={CIRCLES_NAV_MENU_ID}
      className="circles-nav-menu"
      labelledBy={TITLE_ID}
      width={383}
      caretOffset={43}
      rightOffset={23}
    >
      <div className="circles-nav-menu__body">
        <div className="circles-nav-menu__header">
          <span className="circles-nav-menu__symbol">{symbol}</span>
          <div className="circles-nav-menu__header-text">
            <div className="circles-nav-menu__title-row">
              <span className="circles-nav-menu__title" id={TITLE_ID}>
                {COPY.title}
                {/* Reuse the drawer header's beta tag style (globally aggregated
                    SCSS), same as the onboarding card; i18n deferred with the rest
                    of this gated surface. */}
                <sup className="my-circles-header__beta">Beta</sup>
              </span>
              <span className="circles-nav-menu__new">{COPY.new}</span>
            </div>
          </div>
        </div>

        {/* Full panel width, NOT inside __header-text: indented beside the 50px
            symbol this paragraph wraps to ~305px and pushes the joinable circle
            cards below the fold on short viewports. */}
        <p className="circles-nav-menu__subtitle">{COPY.body}</p>

        {/* Sits directly above the offer cards so the "you can leave" promise is
            read before the Join CTAs, not after. */}
        <p className="circles-nav-menu__reassurance" id={REASSURANCE_ID}>
          {COPY.reassurance}
        </p>

        {/* Scroll containment (~3 cards tall, scrolls to ~9) so the panel's
            footprint stays stable as the beta-2 offer grows. Chrome above and
            the opt-out / notice below stay outside the region. */}
        <div className="circles-nav-menu__scroll">
          {renderCards(options)}
        </div>

        <Button
          ref={optOutRef}
          type={TYPES.LINK}
          className="circles-nav-menu__opt-out"
          onClick={handleOptOut}
        >
          {COPY.optOut}
        </Button>

        {noticeDismissed ? null : (
          <div className="circles-nav-menu__notice">
            <p className="circles-nav-menu__notice-title">{noticeTitle}</p>
            <p className="circles-nav-menu__notice-body">{noticeBody}</p>
            <Button
              type={TYPES.LINK}
              className="circles-nav-menu__notice-dismiss"
              onClick={handleDismissNotice}
            >
              {noticeDismiss}
            </Button>
          </div>
        )}
      </div>
    </NavPopover>
  )
}

export const CirclesNavMenu = React.memo(CirclesNavMenuBase)

export default CirclesNavMenu
